/** Thin API client that attaches the Cognito ID token to every request. */
import { fetchAuthSession } from 'aws-amplify/auth';
import type {
  AgencyUser,
  CampaignPayload,
  CampaignResult,
  Conversation,
  FunnelConfig,
  HarbFilesResponse,
  KnowledgeChunk,
  Lead,
  LeadDetail,
  LeadListItem,
  LeadStage,
  MessageTemplate,
  Me,
  Message,
  QaItem,
  QaResyncResult,
  ReplyPair,
  Tenant,
  VehicleInfo,
  AgencyMember,
  MemberSignupStatus,
  Role,
  ChatSimulatorCaps,
} from './types';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

async function authHeader(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeader()),
  };
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    if (res.status === 504 || /gateway timeout/i.test(detail)) {
      throw new Error('504 Gateway Timeout — השרת לא ענה בזמן. נסו שוב בעוד רגע.');
    }
    const clipped = detail.replace(/\s+/g, ' ').trim().slice(0, 180);
    throw new Error(`${res.status} ${res.statusText}${clipped ? `: ${clipped}` : ''}`);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

/** Base path for an agency's member-management endpoints. */
function membersPath(tenantId: string): string {
  return `/api/admin/tenants/${encodeURIComponent(tenantId)}/members`;
}

/** Append ?tenantId= for SystemAdmin cross-tenant access. */
function scoped(path: string, tenantId?: string): string {
  if (!tenantId) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}tenantId=${encodeURIComponent(tenantId)}`;
}

export const api = {
  me: () =>
    req<{ user: Me; chatSimulator?: ChatSimulatorCaps }>('GET', '/api/me').then((r) => ({
      ...r.user,
      chatSimulator: r.chatSimulator,
    })),

  // Agent inbox
  listConversations: (tenantId?: string) =>
    req<{ conversations: Conversation[] }>('GET', scoped('/api/agent/conversations', tenantId)).then(
      (r) => r.conversations,
    ),
  getConversation: (phone: string, tenantId?: string) =>
    req<{ conversation: Conversation; lead: unknown; messages: Message[] }>(
      'GET',
      scoped(`/api/agent/conversations/${encodeURIComponent(phone)}`, tenantId),
    ),
  reply: (phone: string, text: string, tenantId?: string) =>
    req('POST', scoped(`/api/agent/conversations/${encodeURIComponent(phone)}/reply`, tenantId), {
      text,
    }),
  handoff: (phone: string, is_human_handling: boolean, tenantId?: string) =>
    req('POST', scoped(`/api/agent/conversations/${encodeURIComponent(phone)}/handoff`, tenantId), {
      is_human_handling,
    }),
  setBlocked: (phone: string, is_blocked: boolean, tenantId?: string) =>
    req('POST', scoped(`/api/agent/conversations/${encodeURIComponent(phone)}/block`, tenantId), {
      is_blocked,
    }),

  // Leads / CRM
  listLeads: (
    opts: { q?: string; stage?: string; archived?: 'active' | 'archived' | 'all' } = {},
    tenantId?: string,
  ) => {
    const params = new URLSearchParams();
    if (opts.q) params.set('q', opts.q);
    if (opts.stage) params.set('stage', opts.stage);
    if (opts.archived && opts.archived !== 'active') params.set('archived', opts.archived);
    const qs = params.toString();
    return req<{ leads: LeadListItem[] }>(
      'GET',
      scoped(`/api/agent/leads${qs ? `?${qs}` : ''}`, tenantId),
    ).then((r) => r.leads);
  },
  createLead: (lead: Partial<Lead> & { phone: string }, tenantId?: string) =>
    req<{ ok: true; lead: Lead; stage: LeadStage }>(
      'POST',
      scoped('/api/agent/leads', tenantId),
      lead,
    ),
  assignLeads: (phones: string[], assigned_to: string | null, tenantId?: string) =>
    req<{ ok: true; updated: number }>('POST', scoped('/api/agent/leads/assign', tenantId), {
      phones,
      assigned_to,
    }),
  lookupVehicle: (carNumber: string, tenantId?: string) =>
    req<{ vehicle: VehicleInfo }>(
      'GET',
      scoped(`/api/agent/vehicle-lookup/${encodeURIComponent(carNumber)}`, tenantId),
    ).then((r) => r.vehicle),
  archiveLead: (phone: string, tenantId?: string) =>
    req<{ ok: true; lead: Lead }>(
      'POST',
      scoped(`/api/agent/leads/${encodeURIComponent(phone)}/archive`, tenantId),
    ),
  restoreLead: (phone: string, tenantId?: string) =>
    req<{ ok: true; lead: Lead }>(
      'POST',
      scoped(`/api/agent/leads/${encodeURIComponent(phone)}/restore`, tenantId),
    ),
  resetConversation: (phone: string, tenantId?: string) =>
    req<{ ok: true; lead: Lead }>(
      'POST',
      scoped(`/api/agent/leads/${encodeURIComponent(phone)}/reset`, tenantId),
    ),
  importLeads: (
    rows: Array<Record<string, unknown>>,
    mode: 'update' | 'skip',
    tenantId?: string,
  ) =>
    req<{
      ok: true;
      created: number;
      updated: number;
      skipped: number;
      failed: number;
      errors: Array<{ index: number; reason: string }>;
    }>('POST', scoped('/api/agent/leads/import', tenantId), { rows, mode }),
  listTemplates: (tenantId?: string) =>
    req<{ templates: MessageTemplate[] }>(
      'GET',
      scoped('/api/agent/whatsapp/templates', tenantId),
    ).then((r) => r.templates),
  createCampaign: (payload: CampaignPayload, tenantId?: string) =>
    req<CampaignResult>('POST', scoped('/api/agent/campaigns', tenantId), payload),
  getLead: (phone: string, tenantId?: string) =>
    req<LeadDetail>('GET', scoped(`/api/agent/leads/${encodeURIComponent(phone)}`, tenantId)),
  updateLead: (phone: string, updates: Partial<Lead>, tenantId?: string) =>
    req<{ ok: true; lead: Lead; stage: LeadStage }>(
      'PATCH',
      scoped(`/api/agent/leads/${encodeURIComponent(phone)}`, tenantId),
      updates,
    ),
  runQuote: (phone: string, tenantId?: string) =>
    req<{ ok: true; status: 'pending'; lead: Lead }>(
      'POST',
      scoped(`/api/agent/leads/${encodeURIComponent(phone)}/quote`, tenantId),
    ),
  sendQuote: (phone: string, tenantId?: string) =>
    req<{ ok: true; message: Message }>(
      'POST',
      scoped(`/api/agent/leads/${encodeURIComponent(phone)}/quote/send`, tenantId),
    ),
  setNotInterested: (phone: string, not_interested: boolean, tenantId?: string) =>
    req<{ ok: true; lead: Lead; stage: LeadStage }>(
      'POST',
      scoped(`/api/agent/leads/${encodeURIComponent(phone)}/not-interested`, tenantId),
      { not_interested },
    ),
  setOptOut: (phone: string, opt_out: boolean, tenantId?: string) =>
    req<{ ok: true; lead: Lead; stage: LeadStage }>(
      'POST',
      scoped(`/api/agent/leads/${encodeURIComponent(phone)}/opt-out`, tenantId),
      { opt_out },
    ),

  // Har Habituach documents (metadata + authenticated streaming download)
  getHarbFiles: (phone: string, tenantId?: string) =>
    req<HarbFilesResponse>(
      'GET',
      scoped(`/api/agent/leads/${encodeURIComponent(phone)}/harb-files`, tenantId),
    ),
  /**
   * Download one export by its server-side index. A plain <a href> can't carry
   * the Cognito token, so we fetch the stream with the Authorization header into
   * a blob and trigger a browser save.
   */
  downloadHarbFile: async (
    phone: string,
    index: number,
    fileName: string,
    tenantId?: string,
  ): Promise<void> => {
    const path = scoped(
      `/api/agent/leads/${encodeURIComponent(phone)}/harb-files/${index}/download`,
      tenantId,
    );
    const res = await fetch(`${BASE}${path}`, { headers: { ...(await authHeader()) } });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`${res.status} ${res.statusText} ${detail}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'harhabituach.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  // Agency config
  listUsers: (tenantId?: string) =>
    req<{ users: AgencyUser[] }>('GET', scoped('/api/config/users', tenantId)).then((r) => r.users),
  getFunnel: (tenantId?: string) =>
    req<{ funnel: FunnelConfig }>('GET', scoped('/api/config/funnel', tenantId)).then((r) => r.funnel),
  saveFunnel: (funnel: FunnelConfig, tenantId?: string) =>
    req('PUT', scoped('/api/config/funnel', tenantId), { funnel }),
  listKnowledge: (tenantId?: string) =>
    req<{ chunks: KnowledgeChunk[] }>('GET', scoped('/api/config/knowledge', tenantId)).then(
      (r) => r.chunks,
    ),
  addKnowledge: (text: string, source: string | undefined, tenantId?: string) =>
    req('POST', scoped('/api/config/knowledge', tenantId), { text, source }),
  updateKnowledge: (id: string, text: string, source: string | undefined, tenantId?: string) =>
    req('PUT', scoped(`/api/config/knowledge/${encodeURIComponent(id)}`, tenantId), { text, source }),
  deleteKnowledge: (id: string, tenantId?: string) =>
    req('DELETE', scoped(`/api/config/knowledge/${encodeURIComponent(id)}`, tenantId)),
  getReplyBanks: (tenantId?: string) =>
    req<{ banks: Record<string, ReplyPair[]> }>(
      'GET',
      scoped('/api/config/reply-banks', tenantId),
    ).then((r) => r.banks),

  // Unified Q&A (bot-qa): the agency's own editable questions & replies.
  listQaItems: (kind: 'question' | 'reply', tenantId?: string) =>
    req<{ items: QaItem[] }>('GET', scoped(`/api/config/qa/${kind}`, tenantId)).then((r) => r.items),
  upsertQaQuestion: (state: string, lang: 'he' | 'en', text: string, tenantId?: string) =>
    req<{ item: QaItem }>('PUT', scoped('/api/config/qa/question', tenantId), { state, lang, text }),
  createQaReply: (state: string, user: string, bot: string, tenantId?: string) =>
    req<{ item: QaItem }>('POST', scoped('/api/config/qa/reply', tenantId), { state, user, bot }),
  updateQaReply: (id: string, state: string, user: string, bot: string, tenantId?: string) =>
    req<{ item: QaItem }>('PUT', scoped(`/api/config/qa/reply/${encodeURIComponent(id)}`, tenantId), {
      state,
      user,
      bot,
    }),
  deleteQaItem: (id: string, tenantId?: string) =>
    req('DELETE', scoped(`/api/config/qa/${encodeURIComponent(id)}`, tenantId)),
  resyncDefaults: (tenantId?: string) =>
    req<QaResyncResult>('POST', scoped('/api/config/qa/resync', tenantId)),
  deleteCustomerData: (phone: string, tenantId?: string) =>
    req('DELETE', scoped(`/api/config/data/${encodeURIComponent(phone)}`, tenantId)),

  // Chat simulator (SystemAdmin only; backend enforces the role + tenant scope).
  // Drives the bot with a simulated customer message (reply logged, not sent
  // over WhatsApp) and reads back the conversation history.
  devSendMessage: (phone: string, text: string, tenantId?: string) =>
    req<{ ok: true; messageId: string }>('POST', scoped('/api/dev/message', tenantId), {
      phone,
      text,
    }),
  devMessages: (phone: string, tenantId?: string) =>
    req<{ messages: Message[] }>(
      'GET',
      scoped(`/api/dev/messages/${encodeURIComponent(phone)}`, tenantId),
    ).then((r) => r.messages),

  // System admin
  listTenants: () => req<{ tenants: Tenant[] }>('GET', '/api/admin/tenants').then((r) => r.tenants),
  createTenant: (t: Partial<Tenant>) => req<{ tenant: Tenant }>('POST', '/api/admin/tenants', t),
  updateTenant: (id: string, updates: Partial<Tenant>) =>
    req<{ tenant: Tenant }>('PATCH', `/api/admin/tenants/${encodeURIComponent(id)}`, updates),

  // Agency member (dashboard user) management
  listMembers: (tenantId: string) =>
    req<{ members: AgencyMember[] }>('GET', membersPath(tenantId)).then((r) => r.members),
  addMember: (
    tenantId: string,
    member: { email: string; name?: string; mobile?: string; role: Role },
  ) => req<{ member: AgencyMember }>('POST', membersPath(tenantId), member).then((r) => r.member),
  updateMember: (
    tenantId: string,
    userId: string,
    updates: { name?: string; mobile?: string; role?: Role },
  ) =>
    req<{ member: AgencyMember }>('PUT', `${membersPath(tenantId)}/${encodeURIComponent(userId)}`, updates).then(
      (r) => r.member,
    ),
  suspendMember: (tenantId: string, userId: string) =>
    req<{ member: AgencyMember }>(
      'PUT',
      `${membersPath(tenantId)}/${encodeURIComponent(userId)}/suspend`,
    ).then((r) => r.member),
  removeMember: (tenantId: string, userId: string) =>
    req<{ ok: true }>('DELETE', `${membersPath(tenantId)}/${encodeURIComponent(userId)}`),
  getMemberSignupStatus: (tenantId: string, userId: string) =>
    req<{ status: MemberSignupStatus }>(
      'GET',
      `${membersPath(tenantId)}/${encodeURIComponent(userId)}/signup-status`,
    ).then((r) => r.status),
  resendMemberInvite: (tenantId: string, userId: string) =>
    req<{ ok: true }>(
      'POST',
      `${membersPath(tenantId)}/${encodeURIComponent(userId)}/resend-invite`,
    ),

  // Meta onboarding
  metaConfig: () =>
    req<{ appId: string; configId: string; graphVersion: string }>('GET', '/api/meta/config'),
  metaStatus: (tenantId?: string) =>
    req<{ connected: boolean; whatsapp: Record<string, string> }>(
      'GET',
      scoped('/api/meta/status', tenantId),
    ),
  metaConnect: (
    payload: { code: string; wabaId: string; phoneNumberId: string; displayPhoneNumber?: string },
    tenantId?: string,
  ) => req('POST', scoped('/api/meta/connect', tenantId), payload),
};
